import { useState, useEffect, useCallback, useMemo } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Truck, Plus, Loader2, Search, Clock, CheckCircle, AlertTriangle,
  MapPin, Sunrise, Sunset, Zap, Package, ChevronLeft, ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/v2/pickup-requests`;

const STATUS_BADGE = {
  SCHEDULED: "bg-blue-100 text-blue-700",
  ASSIGNED: "bg-orange-100 text-orange-700",
  PICKED: "bg-emerald-100 text-emerald-700",
  PARTIAL: "bg-amber-100 text-amber-700",
  COMPLETED: "bg-emerald-100 text-emerald-700",
  CANCELLED: "bg-red-100 text-red-700",
};

function today(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d;
}

function ymd(d) {
  return d.toISOString().slice(0, 10);
}

export default function PickupRequests() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  // For customer role, default to self; for admin/staff, allow customer selector
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(user?.role === "customer" ? user.id : "");

  // Filters
  const [dateFrom, setDateFrom] = useState(ymd(today(-14)));
  const [dateTo, setDateTo] = useState(ymd(today(0)));
  const [statusFilter, setStatusFilter] = useState("All");
  const [pickupSearch, setPickupSearch] = useState("");

  // Data
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState(1);

  // Step 1 — location
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [showNewLoc, setShowNewLoc] = useState(false);
  const [newLoc, setNewLoc] = useState({
    name: "", address: "", city: "", state: "", pincode: "",
    contact_person: "", contact_phone: "", is_default: false,
  });

  // Step 2 — date + slot
  const [cutoff, setCutoff] = useState(null);
  const [pickupDate, setPickupDate] = useState("");
  const [slot, setSlot] = useState("EVENING");
  const [contactPerson, setContactPerson] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  // Step 3 — shipments
  const [pending, setPending] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [boxCount, setBoxCount] = useState(0);
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.role !== "customer") {
      (async () => {
        try {
          const r = await fetch(`${API}/api/users?role=customer`, { headers });
          if (r.ok) {
            const list = await r.json();
            setCustomers(list);
            if (!customerId && list.length) setCustomerId(list[0].id);
          }
        } catch { /* ignore */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchList = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set("customer_id", customerId);
      if (statusFilter !== "All") p.set("status", statusFilter);
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      const r = await fetch(`${BASE}?${p.toString()}`, { headers });
      if (r.ok) {
        const d = await r.json();
        let arr = d.items || [];
        if (pickupSearch) {
          arr = arr.filter((x) => (x.pickup_id || "").toLowerCase().includes(pickupSearch.toLowerCase()));
        }
        setItems(arr);
      }
    } catch { /* ignore */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, statusFilter, dateFrom, dateTo, pickupSearch]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const summary = useMemo(() => ({
    awaiting: items.filter((i) => i.status === "SCHEDULED" || i.status === "ASSIGNED").length,
    in_transit: items.filter((i) => i.status === "PARTIAL").length,
    completed: items.filter((i) => i.status === "COMPLETED" || i.status === "PICKED").length,
  }), [items]);

  // Open modal — reset state & prefetch data
  const openModal = async () => {
    setStep(1);
    setModalOpen(true);
    setShowNewLoc(false);
    setLocationId("");
    setPickupDate(""); setSlot("EVENING");
    setContactPerson(""); setContactPhone("");
    setSelectedIds(new Set()); setBoxCount(0); setNotes("");

    // Fetch locations
    try {
      const r = await fetch(`${API}/api/v2/pickup-locations/list?owner_id=${customerId}&owner_type=CUSTOMER`, { headers });
      if (r.ok) setLocations(await r.json());
    } catch { /* ignore */ }
    // Fetch cutoff
    try {
      const r = await fetch(`${BASE}/cutoff-check`, { headers });
      if (r.ok) {
        const c = await r.json();
        setCutoff(c);
        setPickupDate(c.suggested_date);
      }
    } catch { /* ignore */ }
  };

  const createLocation = async () => {
    for (const f of ["name", "address", "city", "state", "pincode", "contact_person", "contact_phone"]) {
      if (!newLoc[f]) return toast.error(`${f.replace(/_/g, " ")} is required`);
    }
    try {
      const r = await fetch(`${API}/api/v2/pickup-locations/create`, {
        method: "POST", headers,
        body: JSON.stringify({
          owner_id: customerId,
          owner_type: "CUSTOMER",
          location_name: newLoc.name,
          contact_person: newLoc.contact_person,
          contact_mobile: newLoc.contact_phone,
          address_line1: newLoc.address,
          address_line2: "",
          city: newLoc.city,
          state: newLoc.state,
          pincode: newLoc.pincode,
          location_type: "WAREHOUSE",
          is_default: newLoc.is_default,
        }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success("Location added");
        const g = await fetch(`${API}/api/v2/pickup-locations/list?owner_id=${customerId}&owner_type=CUSTOMER`, { headers });
        if (g.ok) setLocations(await g.json());
        setLocationId(d.location_code);
        setShowNewLoc(false);
      } else {
        toast.error("Failed to create location");
      }
    } catch (e) { toast.error(e.message); }
  };

  const fetchPending = async () => {
    try {
      const r = await fetch(`${BASE}/customer/${customerId}/pending`, { headers });
      if (r.ok) setPending(await r.json());
    } catch { /* ignore */ }
  };

  const nextStep = async () => {
    if (step === 1) {
      if (!locationId) return toast.error("Select a pickup location");
      const loc = locations.find((l) => l.location_code === locationId);
      if (loc) {
        setContactPerson(loc.contact_person || "");
        setContactPhone(loc.contact_mobile || "");
      }
    }
    if (step === 2) {
      if (!pickupDate || !slot) return toast.error("Select date and slot");
      if (!contactPerson || !contactPhone) return toast.error("Contact details required");
      await fetchPending();
    }
    if (step === 3) {
      setBoxCount(selectedIds.size || boxCount || 1);
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(Math.max(1, step - 1));

  const toggleSelect = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
    setBoxCount(s.size);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pending.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(pending.map((p) => p.id)));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = {
        customer_id: customerId,
        pickup_location_id: locationId,
        pickup_date: pickupDate,
        pickup_slot: slot,
        contact_person: contactPerson,
        contact_phone: contactPhone,
        box_count: boxCount || selectedIds.size || 1,
        shipment_ids: Array.from(selectedIds),
        notes: notes || null,
      };
      const r = await fetch(BASE, { method: "POST", headers, body: JSON.stringify(body) });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Pickup Request ${d.pickup_id} created for ${d.pickup_date} ${d.slot_time}`);
        setModalOpen(false);
        fetchList();
      } else {
        const err = await r.json().catch(() => ({ detail: "Create failed" }));
        toast.error(err.detail || "Create failed");
      }
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  const cancelPickup = async (pid) => {
    if (!window.confirm(`Cancel pickup ${pid}?`)) return;
    try {
      const r = await fetch(`${BASE}/${pid}/cancel`, { method: "PATCH", headers });
      if (r.ok) { toast.success("Pickup cancelled"); fetchList(); }
      else toast.error("Cancel failed");
    } catch (e) { toast.error(e.message); }
  };

  const dateSlots = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = today(i);
      arr.push({
        iso: ymd(d),
        dayName: d.toLocaleDateString("en-IN", { weekday: "short" }),
        dayNum: d.getDate(),
        month: d.toLocaleDateString("en-IN", { month: "short" }),
      });
    }
    return arr;
  }, []);

  const selectedLocation = locations.find((l) => l.location_code === locationId);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="pickup-requests">
        {/* HEADER */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Pickup Requests</h1>
            <p className="text-sm text-slate-500">Schedule pickups for your shipments</p>
          </div>
          <Button onClick={openModal} disabled={!customerId} data-testid="create-pickup-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Create Pickup Request
          </Button>
        </div>

        {/* CUSTOMER SELECTOR (admin/staff only) */}
        {user?.role !== "customer" && (
          <div className="bg-white border rounded-lg p-3 flex items-center gap-3">
            <span className="text-xs font-medium text-slate-600">Customer:</span>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
                    className="flex-1 max-w-md border border-slate-300 rounded px-3 py-1.5 text-sm"
                    data-testid="customer-selector">
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name} — {c.email}</option>
              ))}
            </select>
          </div>
        )}

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-3 gap-3">
          <Card icon={Clock} label="Awaiting Pickup" value={summary.awaiting} color="text-blue-600" />
          <Card icon={Truck} label="In Transit" value={summary.in_transit} color="text-amber-600" />
          <Card icon={CheckCircle} label="Completed" value={summary.completed} color="text-emerald-600" />
        </div>

        {/* FILTERS */}
        <div className="bg-white border rounded-lg p-3 grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   className="h-9 text-sm" data-testid="filter-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   className="h-9 text-sm" data-testid="filter-to" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full border border-slate-300 rounded h-9 px-2 text-sm"
                    data-testid="filter-status">
              <option>All</option>
              <option>SCHEDULED</option>
              <option>ASSIGNED</option>
              <option>PICKED</option>
              <option>PARTIAL</option>
              <option>COMPLETED</option>
              <option>CANCELLED</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Search Pickup ID</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
              <Input value={pickupSearch} onChange={(e) => setPickupSearch(e.target.value)}
                     className="h-9 text-sm pl-8" placeholder="Search..." data-testid="filter-search" />
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Pickup ID</th>
                  <th className="text-left px-3 py-2 font-semibold">Requested On</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                  <th className="text-left px-3 py-2 font-semibold">Location</th>
                  <th className="text-center px-3 py-2 font-semibold">Picked / Expected</th>
                  <th className="text-left px-3 py-2 font-semibold">Date</th>
                  <th className="text-left px-3 py-2 font-semibold">Slot</th>
                  <th className="text-right px-3 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="py-10 text-center">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                  </td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={8} className="py-10 text-center text-slate-400">No pickup requests</td></tr>
                ) : items.map((r) => (
                  <tr key={r.pickup_id} className="border-b border-slate-100 hover:bg-slate-50/50"
                      data-testid={`pickup-row-${r.pickup_id}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">{r.pickup_id}</td>
                    <td className="px-3 py-2 text-slate-500">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={`text-[10px] ${STATUS_BADGE[r.status] || "bg-slate-100"}`}>
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">{r.location_name || "—"}</td>
                    <td className="px-3 py-2 text-center">
                      {r.picked_count || 0} / {r.expected_count || 0}
                    </td>
                    <td className="px-3 py-2">{r.pickup_date}</td>
                    <td className="px-3 py-2 text-[11px]">{r.slot_time || r.pickup_slot}</td>
                    <td className="px-3 py-2 text-right">
                      {r.status === "SCHEDULED" && (
                        <Button variant="outline" size="sm" onClick={() => cancelPickup(r.pickup_id)}
                                data-testid={`cancel-${r.pickup_id}`}
                                className="text-red-600 border-red-200">
                          Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* CREATE MODAL */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Pickup Request — Step {step} of 4</DialogTitle>
            </DialogHeader>

            {/* STEP 1 — LOCATION */}
            {step === 1 && (
              <div className="space-y-3" data-testid="step-1">
                <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" /> Pickup Location
                </h3>
                {!showNewLoc && (
                  <>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {locations.length === 0 ? (
                        <p className="text-sm text-slate-400">No saved locations yet.</p>
                      ) : locations.map((loc) => {
                        const id = loc.location_code;
                        return (
                          <label key={id} className={`block border rounded p-3 cursor-pointer ${
                            locationId === id ? "border-blue-500 bg-blue-50" : "border-slate-200"
                          }`}>
                            <div className="flex items-start gap-2">
                              <input type="radio" checked={locationId === id}
                                     onChange={() => setLocationId(id)}
                                     className="mt-1"
                                     data-testid={`loc-${id}`} />
                              <div className="flex-1">
                                <div className="font-semibold text-sm">
                                  {loc.location_name} {loc.is_default && <Badge className="text-[9px] bg-emerald-100 text-emerald-700 ml-1">DEFAULT</Badge>}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {loc.address_line1}, {loc.city} {loc.pincode}
                                </div>
                                <div className="text-xs text-slate-400">
                                  {loc.contact_person} · {loc.contact_mobile}
                                </div>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <Button variant="outline" onClick={() => setShowNewLoc(true)}
                            data-testid="add-new-loc">
                      <Plus className="w-4 h-4 mr-1.5" /> Add New Pickup Location
                    </Button>
                  </>
                )}
                {showNewLoc && (
                  <div className="space-y-2 border rounded p-3 bg-slate-50">
                    <div className="grid grid-cols-2 gap-2">
                      {["name", "address", "city", "state", "pincode", "contact_person", "contact_phone"].map((f) => (
                        <Input key={f} placeholder={f.replace(/_/g, " ")}
                               value={newLoc[f]} onChange={(e) => setNewLoc({ ...newLoc, [f]: e.target.value })}
                               className="h-9 text-sm" data-testid={`new-loc-${f}`} />
                      ))}
                    </div>
                    <label className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={newLoc.is_default}
                             onChange={(e) => setNewLoc({ ...newLoc, is_default: e.target.checked })} />
                      Set as default
                    </label>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => setShowNewLoc(false)}>Cancel</Button>
                      <Button size="sm" onClick={createLocation} data-testid="save-new-loc">Save Location</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* STEP 2 — DATE & SLOT */}
            {step === 2 && (
              <div className="space-y-4" data-testid="step-2">
                {cutoff && (
                  <div className={`rounded p-3 text-xs flex items-center gap-2 ${
                    cutoff.same_day_available
                      ? "bg-blue-50 border border-blue-200 text-blue-800"
                      : "bg-orange-50 border border-orange-200 text-orange-800"
                  }`}>
                    <Clock className="w-4 h-4" />
                    {cutoff.cutoff_message}
                  </div>
                )}

                <div>
                  <label className="text-[10px] uppercase text-slate-500 font-semibold">Pickup Date</label>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {dateSlots.map((d) => {
                      const disabled = !cutoff?.same_day_available && d.iso === cutoff?.suggested_date ? false
                                      : d.iso < (cutoff?.suggested_date || ymd(today(0)));
                      return (
                        <button key={d.iso}
                                disabled={disabled}
                                onClick={() => setPickupDate(d.iso)}
                                data-testid={`date-${d.iso}`}
                                className={`flex-shrink-0 w-20 border rounded p-2 text-center transition ${
                                  pickupDate === d.iso ? "border-blue-500 bg-blue-50"
                                                       : disabled ? "opacity-40 cursor-not-allowed border-slate-200"
                                                                  : "border-slate-200 hover:border-slate-400"
                                }`}>
                          <div className="text-[10px] text-slate-500">{d.dayName}</div>
                          <div className="text-lg font-bold">{d.dayNum}</div>
                          <div className="text-[10px] text-slate-500">{d.month}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase text-slate-500 font-semibold">Slot</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-1">
                    <SlotCard active={slot === "MORNING"} onClick={() => setSlot("MORNING")}
                              icon={Sunrise} label="Morning" time="09:00 - 13:00" testid="slot-morning" />
                    <SlotCard active={slot === "EVENING"} onClick={() => setSlot("EVENING")}
                              icon={Sunset} label="Evening" time="14:00 - 18:00" testid="slot-evening" isDefault />
                    <SlotCard active={slot === "URGENT"} onClick={() => setSlot("URGENT")}
                              icon={Zap} label="Urgent" time="On demand" testid="slot-urgent" warning />
                  </div>
                  {slot === "URGENT" && (
                    <div className="mt-2 bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Urgent pickup charge: ₹500 or ₹25/km (whichever higher) will be added to your bill
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase text-slate-500 font-semibold">Contact Person</label>
                    <Input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)}
                           className="h-9 text-sm" data-testid="contact-person" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-slate-500 font-semibold">Contact Phone</label>
                    <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)}
                           className="h-9 text-sm" data-testid="contact-phone" />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 — SHIPMENTS */}
            {step === 3 && (
              <div className="space-y-3" data-testid="step-3">
                <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                  <Package className="w-4 h-4" /> Select Shipments ({pending.length} available)
                </h3>
                {pending.length === 0 ? (
                  <p className="text-sm text-slate-400">No awaiting shipments found. You can still create a blind pickup.</p>
                ) : (
                  <div className="border rounded max-h-80 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-2 py-2">
                            <input type="checkbox"
                                   checked={selectedIds.size === pending.length && pending.length > 0}
                                   onChange={toggleSelectAll}
                                   data-testid="select-all" />
                          </th>
                          <th className="text-left px-2 py-2">LR / AWB</th>
                          <th className="text-left px-2 py-2">Booked</th>
                          <th className="text-left px-2 py-2">Destination</th>
                          <th className="text-right px-2 py-2">Weight</th>
                          <th className="text-left px-2 py-2">Mode</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pending.map((s) => (
                          <tr key={s.id} className="border-t border-slate-100"
                              data-testid={`ship-${s.id}`}>
                            <td className="px-2 py-1 text-center">
                              <input type="checkbox" checked={selectedIds.has(s.id)}
                                     onChange={() => toggleSelect(s.id)} />
                            </td>
                            <td className="px-2 py-1 font-mono text-[11px]">{s.awb || s.tracking_id || s.id.slice(-6)}</td>
                            <td className="px-2 py-1 text-slate-500">
                              {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                            </td>
                            <td className="px-2 py-1">{s.destination?.city || s.destination?.pincode || "—"}</td>
                            <td className="px-2 py-1 text-right">{s.chargeable_weight || s.package?.weight || 0} kg</td>
                            <td className="px-2 py-1">{s.shipping_mode || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <label className="text-xs text-slate-600">Box Count:</label>
                  <Input type="number" value={boxCount} onChange={(e) => setBoxCount(parseInt(e.target.value) || 0)}
                         className="w-24 h-9 text-sm" data-testid="box-count" />
                  <span className="text-xs text-slate-500">
                    {selectedIds.size} shipments selected · {boxCount} boxes
                  </span>
                </div>
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                          placeholder="Notes (optional)" data-testid="notes" />
                <p className="text-[11px] text-slate-500 italic">
                  Shipments without a pickup request will not be picked up. Select all ready shipments.
                </p>
              </div>
            )}

            {/* STEP 4 — CONFIRM */}
            {step === 4 && (
              <div className="space-y-3" data-testid="step-4">
                <h3 className="text-sm font-bold text-slate-700">Review & Confirm</h3>
                <div className="bg-slate-50 border rounded p-3 text-sm space-y-2">
                  <Row label="Location" value={selectedLocation ? `${selectedLocation.location_name} — ${selectedLocation.city} ${selectedLocation.pincode}` : "—"} />
                  <Row label="Date" value={pickupDate} />
                  <Row label="Slot" value={`${slot} (${slot === "MORNING" ? "09:00 - 13:00" : slot === "EVENING" ? "14:00 - 18:00" : "On demand"})`} />
                  <Row label="Contact" value={`${contactPerson} · ${contactPhone}`} />
                  <Row label="Shipments" value={`${selectedIds.size} selected`} />
                  <Row label="Boxes" value={boxCount || selectedIds.size || 1} />
                  {slot === "URGENT" && <Row label="Urgent Charge" value="₹500 (or ₹25/km)" color="text-red-600" />}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={prevStep} disabled={step === 1}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              {step < 4 ? (
                <Button onClick={nextStep} data-testid="next-step-btn">
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={submit} disabled={submitting}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="submit-pickup-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1.5" />}
                  Create Pickup Request
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function Card({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="text-xs text-slate-500 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function SlotCard({ active, onClick, icon: Icon, label, time, testid, warning, isDefault }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`border rounded p-3 text-left transition ${
              active ? (warning ? "border-red-500 bg-red-50" : "border-blue-500 bg-blue-50")
                     : "border-slate-200 hover:border-slate-400"
            }`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="font-semibold text-sm">{label}</span>
        {isDefault && <Badge className="text-[9px] bg-slate-100 text-slate-600">DEFAULT</Badge>}
      </div>
      <div className="text-xs text-slate-500 mt-1">{time}</div>
    </button>
  );
}

function Row({ label, value, color }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className={`font-medium ${color || "text-slate-800"}`}>{value}</span>
    </div>
  );
}
